import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ClientSidebar from '../../components/ClientSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { justificatifService, loanService, clientService, contractService } from '../../services/api';
import { CreditCard, FileText, AlertCircle, CheckCircle, Clock, XCircle, Upload, Info, Ban, Download, Plus, Menu } from 'lucide-react';

// Labels pour la page "Mes justificatifs" dans la sidebar (5 types)
const JUSTIFICATIF_LABELS = {
  'piece_identite': { label: 'Pièce d\'identité', icon: '🪪' },
  'rib': { label: 'RIB', icon: '🏦' },
  'justificatif_domicile': { label: 'Justificatif de domicile', icon: '🏠' },
  'avis_imposition': { label: 'Avis d\'imposition', icon: '📋' },
  'releve_compte': { label: 'Relevé de compte', icon: '📊' }
};

// Labels pour la section "Justificatifs envoyés" sur le Dashboard (4 types de base + 2 conditionnels)
const DASHBOARD_JUSTIFICATIFS = [
  { 
    id: 'justificatif_identite', 
    label: 'Justificatif d\'identité', 
    icon: '🪪',
    redirectTo: '/client/identity-upload' // Page upload recto/verso ou passeport
  },
  { 
    id: 'releve_identite_bancaire', 
    label: 'Relevé d\'identité bancaire', 
    icon: '🏦',
    redirectTo: '/client/rib-upload' // Page upload RIB PDF
  },
  { 
    id: 'releve_compte', 
    label: 'Relevé de compte', 
    icon: '📊',
    iconImage: 'https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/bna1hv27_95FA2AF3-3DFF-4C1A-9D26-55AACD78A95A.png',
    redirectTo: '/client/loan-flow/bank-connection' // Connexion bancaire (NE PAS TOUCHER)
  },
  { 
    id: 'avis_imposition', 
    label: 'Avis d\'imposition', 
    icon: '📋',
    redirectTo: '/client/loan-flow/mtrust-intro' // MTrust → Impôts (NE PAS TOUCHER)
  },
  { 
    id: 'justificatif_domicile', 
    label: 'Justificatif de domicile', 
    icon: '🏠',
    redirectTo: '/client/domicile-upload' // Page upload justificatif domicile
  },
  { 
    id: 'bulletin_salaire', 
    label: 'Bulletin de salaire', 
    icon: '💼',
    redirectTo: '/client/bulletin-salaire-upload', // Page upload bulletin salaire
    requiresSalary: true // Afficher uniquement si le client a un salaire
  }
];

// Statuts professionnels qui n'ont PAS de salaire
const NO_SALARY_STATUSES = ['chomeur', 'sans_emploi', 'retraite', 'etudiant', 'sans_activite'];

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [justificatifs, setJustificatifs] = useState([]);
  const [loans, setLoans] = useState([]);
  const [clientData, setClientData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadClientData();
      loadJustificatifs();
      loadLoans();
      loadContracts();
    }
  }, [user?.id]);

  // Load admin-uploaded contracts
  const loadContracts = async () => {
    try {
      const data = await contractService.getByClient(user.id);
      setContracts(data);
    } catch (error) {
      console.error('Error loading contracts:', error);
    }
  };

  // Fetch fresh client data from server
  const loadClientData = async () => {
    try {
      const data = await clientService.getById(user.id);
      setClientData(data);
      // Update local storage with fresh data
      updateUser(data);
    } catch (error) {
      console.error('Error loading client data:', error);
    }
  };

  const loadLoans = async () => {
    try {
      const data = await loanService.getByClient(user.id);
      setLoans(data);
    } catch (error) {
      console.error('Error loading loans:', error);
    }
  };

  // Use fresh data from server, fallback to user context
  const loanAmount = clientData?.loanAmount || user?.loanAmount || 20000;
  const monthlyPayment = clientData?.monthlyPayment || user?.monthlyPayment || 350;
  const interestRate = clientData?.interestRate || user?.interestRate || 5.9;
  const numberOfPayments = clientData?.numberOfPayments || user?.numberOfPayments || null;
  const loanStatus = clientData?.loanStatus || user?.loanStatus || 'pending';

  // Loan status banner configuration
  const getLoanStatusBanner = () => {
    switch (loanStatus) {
      case 'active':
        return {
          bg: 'bg-green-500',
          icon: CheckCircle,
          title: 'Prêt Actif',
          message: 'Votre prêt est actif. Les fonds ont été débloqués.'
        };
      case 'blocked':
        return {
          bg: 'bg-red-500',
          icon: Ban,
          title: 'Prêt Bloqué',
          message: 'Votre prêt est actuellement bloqué. Veuillez contacter votre conseiller.'
        };
      default:
        return {
          bg: 'bg-yellow-500',
          icon: Clock,
          title: 'En cours de traitement',
          message: 'Votre demande est en cours d\'examen. Complétez votre dossier pour accélérer le processus.'
        };
    }
  };

  const statusBanner = getLoanStatusBanner();

  // Steps dynamiques basés sur le statut du prêt
  const getSteps = () => {
    if (loanStatus === 'active') {
      // Prêt actif = toutes les étapes complétées
      return [
        { id: 1, label: 'Constitution du dossier', status: 'completed' },
        { id: 2, label: 'En cours d\'étude', status: 'completed' },
        { id: 3, label: 'Acceptation', status: 'completed' },
        { id: 4, label: 'Envoi des fonds', status: 'completed' }
      ];
    } else if (loanStatus === 'blocked') {
      // Prêt bloqué = arrêté à l'acceptation
      return [
        { id: 1, label: 'Constitution du dossier', status: 'completed' },
        { id: 2, label: 'En cours d\'étude', status: 'blocked' },
        { id: 3, label: 'Acceptation', status: 'pending' },
        { id: 4, label: 'Envoi des fonds', status: 'pending' }
      ];
    } else {
      // En attente = en cours de constitution
      return [
        { id: 1, label: 'Constitution du dossier', status: 'completed' },
        { id: 2, label: 'En cours d\'étude', status: 'active' },
        { id: 3, label: 'Acceptation', status: 'pending' },
        { id: 4, label: 'Envoi des fonds', status: 'pending' }
      ];
    }
  };

  const steps = getSteps();

  // Calcul du pourcentage de progression
  const getProgressPercentage = () => {
    if (loanStatus === 'active') return 100;
    if (loanStatus === 'blocked') return 25;
    return 50; // pending
  };

  // Infos dynamiques pour la box en bas
  const getInfoBoxContent = () => {
    if (loanStatus === 'active') {
      return {
        title: 'Dossier validé',
        message: 'Votre dossier a été accepté et les fonds ont été envoyés sur votre compte.',
        bgColor: '#D1FAE5', // vert clair
        iconColor: '#10B981' // vert
      };
    } else if (loanStatus === 'blocked') {
      return {
        title: 'Dossier en attente',
        message: 'Votre dossier est en cours d\'examen. Veuillez contacter votre conseiller pour plus d\'informations.',
        bgColor: '#FEE2E2', // rouge clair
        iconColor: '#EF4444' // rouge
      };
    } else {
      return {
        title: 'Constitution du dossier',
        message: 'Complétez et signez votre dossier en ligne. C\'est simple, rapide et sécurisé !',
        bgColor: '#E0F7FA', // cyan clair
        iconColor: '#8B5CF6' // cyan
      };
    }
  };

  const infoBox = getInfoBoxContent();

  const loadJustificatifs = async () => {
    try {
      const data = await justificatifService.getByClient(user.id);
      setJustificatifs(data);
    } catch (error) {
      console.error('Error loading justificatifs:', error);
    }
  };

  const getJustificatifStatus = (type) => {
    return justificatifs.find(j => j.type === type);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Validé':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'En attente':
        return <Clock className="h-5 w-5 text-orange-500" />;
      case 'Refusé':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <XCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Validé': 'bg-green-100 text-green-700',
      'En attente': 'bg-orange-100 text-orange-700',
      'Refusé': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Download contract
  const handleDownloadContract = async (contract) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      window.open(`${API_URL}/api/admin-contracts/file/${contract.fileName}`, '_blank');
    } catch (error) {
      console.error('Error downloading contract:', error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex flex-1">
        <ClientSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
        <div className="flex-1 bg-white">
        {/* Mobile Header - Black to match logo */}
        <div className="md:hidden bg-black text-white p-4 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/client/dashboard">
            <img 
              src="https://customer-assets.emergentagent.com/job_younited-clone-1/artifacts/y6hpz6h2_IMG_0359.jpeg"
              alt="Younited"
              className="h-8 object-contain"
            />
          </Link>
          <div className="w-10"></div>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block bg-gray-800 text-white py-3 px-8 text-sm">
          Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          
          {/* Welcome message with status */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Bonjour {user?.firstName || 'Client'},
            </h1>
            <p className="text-gray-600 text-sm md:text-base">
              {loanStatus === 'active' 
                ? 'votre dossier a été validé.' 
                : loanStatus === 'blocked'
                ? 'votre dossier est en attente de vérification.'
                : 'votre dossier est en cours d\'étude.'}
            </p>
            {loanStatus === 'pending' && (
              <Card className="mt-4 p-3 md:p-4 bg-cyan-50 border-cyan-200">
                <div className="flex items-center space-x-3">
                  <span className="text-xl md:text-2xl">😊</span>
                  <p className="text-xs md:text-sm text-gray-700">
                    Nos équipes étudient votre dossier, vous recevrez une confirmation de validation sous <strong>5 jours ouvrés</strong>.
                  </p>
                </div>
              </Card>
            )}
          </div>

          {/* ===== TABS + CONTENT COMME SOFINCO ===== */}
          <Tabs defaultValue="justificatifs" className="mb-8">
            <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none p-0 h-auto overflow-x-auto">
              <TabsTrigger
                value="justificatifs"
                className="data-[state=active]:border-b-2 data-[state=active]:border-purple-600 data-[state=active]:text-purple-600 rounded-none px-3 md:px-6 py-3 text-gray-600 text-xs md:text-sm whitespace-nowrap"
              >
                Mes justificatifs
              </TabsTrigger>
              <TabsTrigger
                value="contracts"
                className="data-[state=active]:border-b-2 data-[state=active]:border-purple-600 data-[state=active]:text-purple-600 rounded-none px-3 md:px-6 py-3 text-gray-600 text-xs md:text-sm whitespace-nowrap"
              >
                Vos contrats Younited
              </TabsTrigger>
            </TabsList>

            {/* Tab: Mes justificatifs - Grille 2x2 comme Sofinco */}
            <TabsContent value="justificatifs" className="mt-6">
              <Card className="p-4 md:p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4 md:mb-6 text-sm md:text-base">Justificatifs envoyés</h3>
                
                {/* Grid dynamique */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {DASHBOARD_JUSTIFICATIFS
                    .filter(item => {
                      // Si l'item requiert un salaire, vérifier le statut professionnel
                      if (item.requiresSalary) {
                        const employmentStatus = clientData?.employmentStatus || user?.employmentStatus;
                        // Masquer si pas de statut ou si le statut est dans la liste "sans salaire"
                        if (!employmentStatus || NO_SALARY_STATUSES.includes(employmentStatus)) {
                          return false;
                        }
                      }
                      return true;
                    })
                    .map((item) => {
                    const justif = getJustificatifStatus(item.id);
                    const isValidated = justif?.status === 'Validé';
                    const hasDocument = !!justif;

                    return (
                      <div 
                        key={item.id} 
                        className="flex flex-col items-center p-3 md:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => !isValidated && navigate(item.redirectTo)}
                      >
                        {/* Icon */}
                        <div className="w-10 h-10 md:w-14 md:h-14 bg-white rounded-lg flex items-center justify-center mb-2 md:mb-3 shadow-sm">
                          {item.iconImage ? (
                            <img src={item.iconImage} alt={item.label} className="h-6 w-6 md:h-8 md:w-8 object-contain" />
                          ) : (
                            <span className="text-lg md:text-2xl">{item.icon}</span>
                          )}
                        </div>
                        
                        {/* Label */}
                        <p className="text-xs md:text-sm text-gray-800 text-center mb-2 md:mb-3 leading-tight font-medium h-8 md:h-10 flex items-center">
                          {item.label}
                        </p>
                        
                        {/* Status - Checkmark vert comme Sofinco */}
                        {isValidated ? (
                          <CheckCircle className="h-5 w-5 md:h-7 md:w-7 text-cyan-500" />
                        ) : hasDocument ? (
                          <Clock className="h-5 w-5 md:h-7 md:w-7 text-orange-500" />
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-purple-600 text-xs px-2 md:px-3 py-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(item.redirectTo);
                            }}
                          >
                            <Plus className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                            Ajouter
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            </TabsContent>

            {/* Tab: Vos contrats Younited */}
            <TabsContent value="contracts" className="mt-6">
              <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
                {/* ASA Section */}
                <Card className="p-4 md:p-6 border border-gray-200">
                  <div className="mb-4 md:mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-lg md:text-xl font-bold text-gray-900">
                        Rachat de crédit de {loanAmount?.toLocaleString() || '20 000'} €
                      </h2>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 md:px-3 py-1 text-xs rounded-full font-medium ${
                        loanStatus === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : loanStatus === 'blocked'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {loanStatus === 'active' 
                          ? 'Younited - Crédit validé ✓' 
                          : loanStatus === 'blocked'
                          ? 'Younited - Crédit bloqué'
                          : 'Younited - Rachat de crédit en cours'}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs md:text-sm mt-2">N° {clientData?.accountNumber || user?.accountNumber || '------'}</p>
                    
                    {/* Mensualité et Taux d'intérêt */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Mensualité :</span>
                        <span className="font-semibold text-gray-900">{monthlyPayment?.toLocaleString() || '350'} €/mois</span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-gray-600">Taux d'intérêt :</span>
                        <span className="font-semibold text-gray-900">{interestRate || '5,9'} %</span>
                      </div>
                      {numberOfPayments && (
                        <div className="flex justify-between items-center text-sm mt-1">
                          <span className="text-gray-600">Nombre de mensualités :</span>
                          <span className="font-semibold text-gray-900">{numberOfPayments}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Progress Tracker */}
                  <div className="mb-4 md:mb-6">
                    <h3 className="font-semibold text-gray-900 mb-4 md:mb-6 text-sm md:text-base">Suivi de votre demande</h3>
                    
                    {/* Steps with circles */}
                    <div className="relative mb-4 md:mb-6">
                      <div className="flex items-start justify-between relative">
                        {/* Connection lines */}
                        <div className="absolute top-4 md:top-5 left-0 right-0 h-0.5 bg-gray-200" style={{ margin: '0 8%' }}></div>
                        
                        {steps.map((step) => (
                          <div key={step.id} className="flex flex-col items-center flex-1 relative z-10">
                            {/* Circle */}
                            <div
                              className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold shadow ${
                                step.status === 'completed'
                                  ? 'bg-green-500 text-white'
                                  : step.status === 'active'
                                  ? 'text-white'
                                  : step.status === 'blocked'
                                  ? 'bg-red-500 text-white'
                                  : 'bg-gray-200 text-gray-500'
                              }`}
                              style={step.status === 'active' ? { backgroundColor: '#8B5CF6' } : {}}
                            >
                              {step.status === 'completed' ? (
                                <CheckCircle className="h-4 w-4 md:h-6 md:w-6" />
                              ) : step.status === 'active' ? (
                                <CheckCircle className="h-4 w-4 md:h-6 md:w-6" />
                              ) : step.status === 'blocked' ? (
                                <XCircle className="h-4 w-4 md:h-6 md:w-6" />
                              ) : (
                                <span className="w-2 h-2 md:w-3 md:h-3 bg-gray-400 rounded-full"></span>
                              )}
                            </div>
                            {/* Label */}
                            <p className="text-[10px] md:text-xs text-gray-600 text-center mt-1 md:mt-2 max-w-[60px] md:max-w-[80px] leading-tight">
                              {step.label}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4 md:mb-6">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-2 rounded-full transition-all duration-700"
                          style={{
                            width: `${getProgressPercentage()}%`,
                            backgroundColor: loanStatus === 'active' ? '#10B981' : loanStatus === 'blocked' ? '#EF4444' : '#8B5CF6'
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Current Step Info Box */}
                    <Card className="p-3 md:p-4 border-0" style={{ backgroundColor: infoBox.bgColor }}>
                      <div className="flex items-start space-x-2 md:space-x-3">
                        <div className="p-1.5 md:p-2 rounded-full flex-shrink-0" style={{ backgroundColor: infoBox.iconColor }}>
                          {loanStatus === 'active' ? (
                            <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-white" />
                          ) : loanStatus === 'blocked' ? (
                            <AlertCircle className="h-3 w-3 md:h-4 md:w-4 text-white" />
                          ) : (
                            <Info className="h-3 w-3 md:h-4 md:w-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 text-xs md:text-sm mb-1">{infoBox.title}</h4>
                          <p className="text-[10px] md:text-xs text-gray-700 leading-relaxed">
                            {infoBox.message}
                          </p>
                        </div>
                        {loanStatus !== 'active' && (
                          <div className="flex items-center space-x-1 text-[10px] md:text-xs text-gray-700 flex-shrink-0">
                            <Clock className="h-3 w-3" />
                            <span className="font-semibold">10 jours</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                </Card>

                {/* Contrat Section */}
                <Card className="p-4 md:p-6 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4 text-sm md:text-base">Contrat</h3>
                  {contracts.length > 0 ? (
                    <div className="space-y-3">
                      {contracts.map((contract) => (
                        <div key={contract.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                              <FileText className="h-4 w-4 md:h-5 md:w-5 text-gray-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 text-xs md:text-sm truncate">{contract.originalName || 'Contrat de prêt'}</p>
                              <p className="text-[10px] md:text-xs text-gray-500 truncate">{contract.contractType}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-purple-600 border-purple-300 text-xs px-2 md:px-3"
                              onClick={() => handleDownloadContract(contract)}
                            >
                              <span className="hidden md:inline">Télécharger</span>
                              <Download className="h-4 w-4 md:hidden" />
                            </Button>
                            <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-cyan-500" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <FileText className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-xs md:text-sm">Contrat en cours de préparation</p>
                      </div>
                      <Clock className="h-5 w-5 md:h-6 md:w-6 text-orange-500" />
                    </div>
                  )}
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>

      {/* Footer Younited - Full Width */}
      <footer className="bg-black text-white w-full">
        <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Younited Logo */}
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/q9qswhf3_YOUNITED.svg" 
                alt="Younited" 
                className="h-6 w-auto"
              />
            </div>

            {/* Legal text */}
            <div className="text-gray-400 text-xs leading-relaxed mb-6 space-y-4">
              <p>
                Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.
              </p>
            </div>

            {/* Regulatory information */}
            <div className="text-gray-400 text-xs leading-relaxed mb-6 space-y-2">
              <p>
                Younited SA, établissement de crédit de droit français, agréé par l'Autorité de contrôle prudentiel et de résolution (ACPR) sous le n° 16488.
              </p>
              <p>
                Les emprunteurs sont couverts par une assurance Décès, Incapacité de travail, Invalidité Permanente Totale (DIT) souscrite auprès de MetLife Europe d.a.c. – MetLife Europe Insurance d.a.c. sont des filiales de MetLife, Inc. MetLife Europe d.a.c. est agréée et réglementée par la Central Bank of Ireland.
              </p>
              <p>
                Younited (France) S.A. est enregistrée sur le registre unique géré par l'ORIAS sous le n° 11061269 en qualité de courtier en assurances.
              </p>
              <p>
                Taux Annuel Effectif Global (TAEG) fixe à partir de 1,20 %, pour une durée comprise entre 6 et 180 mois, exclusivement dédié au financement de projets de rénovation énergétique ou au rachat de crédits liés à des travaux énergétiques, valable uniquement pour l'année 2026.
              </p>
            </div>

            {/* Company info */}
            <div className="text-gray-400 text-xs leading-relaxed mb-8">
              <p>
                Younited (France) SA au capital de 3 396 476€ – Siège : 21 rue de Châteaudun — 75009 PARIS – RCS Paris : 517 586 376
              </p>
            </div>

            {/* B Corp Logo and Social */}
            <div className="flex flex-col md:flex-row items-center justify-between pt-6 border-t border-gray-800">
              {/* B Corp certified */}
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="bg-white rounded-full p-2">
                  <svg className="w-6 h-6 text-black" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm0-8h-2V7h2v2zm4 8h-2v-4h2v4zm0-6h-2V9h2v2z"/>
                  </svg>
                </div>
                <span className="text-white text-xs font-medium">Entreprise B Certifiée</span>
              </div>

              {/* Country flags */}
              <div className="flex items-center space-x-2 mb-4 md:mb-0">
                <span className="text-lg">🇵🇹</span>
                <span className="text-lg">🇫🇷</span>
                <span className="text-lg">🇮🇹</span>
                <span className="text-lg">🇪🇸</span>
                <span className="text-lg">🇩🇪</span>
              </div>

              {/* Social media icons */}
              <div className="flex items-center space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="mt-6 pt-4 border-t border-gray-800 text-center">
              <p className="text-gray-500 text-xs">younited-credit.com — Privée</p>
            </div>
          </div>
        </footer>
    </div>
  );
};

export default Dashboard;
